import { differenceInCalendarDays, format, startOfDay } from "date-fns";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import { Controller, UseFormReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldError } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { IHotelsSearchSchema } from "../../types/hotel-search-type";

interface CheckInCheckOutDateProps {
  form: UseFormReturn<IHotelsSearchSchema>;
}

export default function CheckInCheckOutDate({
  form,
}: CheckInCheckOutDateProps) {
  const [open, setOpen] = useState(false);

  const fromError = form.formState.errors.date?.from;
  const toError = form.formState.errors.date?.to;
  const error = fromError?.message || toError?.message;

  return (
    <Controller
      control={form.control}
      name="date"
      render={({ field, fieldState }) => {
        const date = field.value as DateRange | undefined;
        const checkInDate = date?.from as Date;
        const checkOutDate = date?.to as Date;

        const handleSelect = (
          _selected: DateRange | undefined,
          selectedDay: Date,
        ) => {
          const currentSelection = date;

          if (!currentSelection?.from) {
            field.onChange({ from: selectedDay, to: undefined });
            return;
          }

          if (currentSelection.from && currentSelection.to) {
            field.onChange({ from: selectedDay, to: undefined });
            return;
          }

          if (currentSelection.from && !currentSelection.to) {
            const fromDate = currentSelection.from as Date;
            const clickedDate = selectedDay;

            if (clickedDate.getTime() === fromDate.getTime()) {
              field.onChange(undefined);
            } else if (clickedDate > fromDate) {
              field.onChange({ from: fromDate, to: clickedDate });
            } else {
              field.onChange({ from: clickedDate, to: undefined });
            }
          }
        };

        return (
          <Field data-invalid={fieldState.invalid || Boolean(error)}>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  aria-invalid={fieldState.invalid || Boolean(error)}
                  className={cn(
                    "h-14 w-full rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
                    !checkInDate && !checkOutDate && "text-muted-foreground",
                    (fieldState.invalid || Boolean(error)) &&
                      "border-destructive",
                  )}
                >
                  {checkInDate ? (
                    <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
                      <span className="text-[1.5rem] font-bold leading-none tracking-tight text-foreground sm:text-[1.75rem]">
                        {format(checkInDate, "dd")}
                      </span>
                      <span className="h-9 w-px bg-border" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-foreground/85">
                          {format(checkInDate, "MMMM")}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {checkOutDate
                            ? `${differenceInCalendarDays(checkOutDate, checkInDate)} night${differenceInCalendarDays(checkOutDate, checkInDate) === 1 ? "" : "s"} · Checkout ${format(checkOutDate, "dd MMM, yyyy")}`
                            : "Select checkout date"}
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className="min-w-0 text-left">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                        Stay Dates
                      </span>
                      <span className="block truncate text-sm text-muted-foreground">
                        Select dates
                      </span>
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: checkInDate, to: checkOutDate }}
                  onSelect={handleSelect}
                  numberOfMonths={2}
                  disabled={(value) => value < startOfDay(new Date())}
                  className="w-full"
                />
              </PopoverContent>
            </Popover>
            <FieldError errors={[fromError, toError]} />
          </Field>
        );
      }}
    />
  );
}
